import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import 'mapbox-gl/dist/mapbox-gl.css';
import { Helmet } from "react-helmet-async";

// --- Layout & Core Components ---
import Header from "./components/Header";
import Layout from "./components/Layout";
import ScrollToTop from "./ScrollToTop";

// --- Page Components (統合版) ---
// ※ 今後、各ファイル内で useParams() を使って言語判定を行ってください
const Home = lazy(() => import("./components/Home"));
const About = lazy(() => import("./components/About"));
const Partners = lazy(() => import("./components/Partners"));
const Pricing = lazy(() => import("./components/Pricing"));
const Contact = lazy(() => import("./components/Contact"));
const IssuePage = lazy(() => import('./components/IssuePage'));
const Terms = lazy(() => import("./components/Terms"));
const Privacy = lazy(() => import("./components/Privacy"));
const SpecifiedCommercial = lazy(() => import("./components/SpecifiedCommercialTransaction"));
const ProtectedMapRoute = lazy(() => import("./components/ProtectedMapRoute"));
const NotFound = lazy(() => import("./components/NotFound"));
const UseCases = lazy(() => import("./components/UseCases")); // 追加

// --- List & Detail Pages (統合版) ---
const MapListPage = lazy(() => import('./pages/MapListPage'));
const NewsListPage = lazy(() => import('./pages/NewsListPage'));
const NewsArticlePage = lazy(() => import('./pages/NewsArticlePage'));
const ArticleListPage = lazy(() => import('./pages/ArticleListPage'));
const ArticleDetailPage = lazy(() => import('./pages/ArticleDetailPage'));
const ToiletReviewPage = lazy(() => import('./pages/ToiletReviewPage'));
const HowToUseToilets = lazy(() => import('./components/HowToUseToilets'));

// --- Redirect Helpers ---
// 記事やマップのIDのみでアクセスされた場合、日本語(/ja)へ転送する
const RedirectToJa = ({ type }) => {
  const params = useParams();
  if (type === 'news') return <Navigate to={`/news/${params.articleSlug}/ja`} replace />;
  if (type === 'article') return <Navigate to={`/article/${params.slug}/ja`} replace />;
  if (type === 'toilet') return <Navigate to={`/toilet/${params.toiletId}/ja`} replace />;
  if (type === 'map') return <Navigate to={`/map/${params.prefectureSlug}/${params.citySlug}/${params.eventSlug}/ja`} replace />;
  return <Navigate to="/" replace />;
};

// Homeコンポーネントに lang パラメータを渡すためのラッパー
// (Home.jsx が props で lang を受け取る仕様のままでも動くようにします)
const HomeWrapper = () => {
  const { lang } = useParams();
  return <Home lang={lang || 'ja'} />;
};

function App() {
  return (
    <>
      <Helmet>
        <script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9047662673368492" crossOrigin="anonymous"></script>
      </Helmet>

      <BrowserRouter>
        <ScrollToTop />
        <Header />

        {/* ヘッダー固定分の余白確保 */}
        <main style={{ paddingTop: '64px', minHeight: '100vh' }}>
          <Suspense fallback={<div>Loading...</div>}>
            <Routes>
              <Route path="/" element={<Layout />}>

                {/* --- Home Page --- */}
                {/* ルートアクセスは日本語扱い */}
                <Route index element={<Home lang="ja" />} />
                {/* /ja はルートへ正規化 */}
                <Route path="/ja" element={<Navigate to="/" replace />} />
                {/* その他の言語 (/en, /zh, etc.) */}
                <Route path="/:lang" element={<HomeWrapper />} />



                {/* マップ表示 (言語あり) */}
                <Route path="/map/:prefectureSlug/:citySlug/:eventSlug/:lang" element={<ProtectedMapRoute />} />
                <Route path="/map/:prefectureSlug/:citySlug?/:lang" element={<ProtectedMapRoute />} />

                {/* --- Map Pages --- */}
                {/* パラメータ不足のリダイレクト */}
                <Route path="/map/:prefectureSlug/:citySlug/:eventSlug" element={<RedirectToJa type="map" />} />

                {/* マップ一覧 */}
                <Route path="/maplist" element={<Navigate to="/maplist/ja" replace />} />
                <Route path="/maplist/:lang" element={<MapListPage />} />


                {/* --- Static Pages (About, Partners, etc.) --- */}
                {/* 言語なしアクセスは /ja へリダイレクト、ありならコンポーネント表示 */}

                <Route path="/about" element={<Navigate to="/about/ja" replace />} />
                <Route path="/about/:lang" element={<About />} />

                {/* 1. 言語なし(/cases)で来たら /cases/ja へ転送 */}
                <Route path="/cases" element={<Navigate to="/cases/ja" replace />} />
                {/* 2. 言語あり(/cases/ja, /cases/en)で UseCases を表示 */}
                <Route path="/cases/:lang" element={<UseCases />} />

                <Route path="/partners" element={<Navigate to="/partners/ja" replace />} />
                <Route path="/partners/:lang" element={<Partners />} />

                <Route path="/pricing" element={<Navigate to="/pricing/ja" replace />} />
                <Route path="/pricing/:lang" element={<Pricing />} />

                <Route path="/contact" element={<Navigate to="/contact/ja" replace />} />
                <Route path="/contact/:lang" element={<Contact />} />

                <Route path="/issue" element={<Navigate to="/issue/ja" replace />} />
                <Route path="/issue/:lang" element={<IssuePage />} />

                <Route path="/how-to-use-toilets" element={<Navigate to="/how-to-use-toilets/ja" replace />} />
                <Route path="/how-to-use-toilets/:lang" element={<HowToUseToilets />} />


                {/* --- News & Articles --- */}
                {/* News List */}
                <Route path="/news" element={<Navigate to="/news/ja" replace />} />
                <Route path="/news/:lang" element={<NewsListPage />} /> {/* ※詳細ページと被らないよう注意が必要ですが、詳細にはslugが入るため通常は区別されます */}

                {/* News Detail ( :lang が末尾に来るパターン ) */}
                {/* 注意: /news/some-slug (言語なし) */}
                <Route path="/news/:articleSlug" element={<RedirectToJa type="news" />} />
                {/* /news/some-slug/en (言語あり) */}
                <Route path="/news/:articleSlug/:lang" element={<NewsArticlePage />} />

                {/* Article List */}
                <Route path="/article" element={<Navigate to="/article/ja" replace />} />
                <Route path="/article/:lang" element={<ArticleListPage />} />

                {/* Article Detail */}
                <Route path="/article/:slug" element={<RedirectToJa type="article" />} />
                <Route path="/article/:slug/:lang" element={<ArticleDetailPage />} />


                {/* --- Review & Other --- */}
                <Route path="/toilet/:toiletId" element={<RedirectToJa type="toilet" />} />
                <Route path="/toilet/:toiletId/:lang" element={<ToiletReviewPage />} />


                {/* --- Legal (言語対応不要、または内部で切り替え) --- */}
                <Route path="terms" element={<Terms />} />
                <Route path="privacy" element={<Privacy />} />
                <Route path="specifiedCommercialTransactionForToimap" element={<SpecifiedCommercial />} />

                {/* 404 */}
                <Route path="*" element={<NotFound />} />

              </Route>
            </Routes>
          </Suspense>
        </main>
      </BrowserRouter>
    </>
  );
}

export default App;